import classNames from 'classnames';
import queryString from 'query-string';
import { useSearch } from 'wouter';

import useFiltersQuery from '../../hooks/useFiltersQuery';
import useScrollEnter from '../../hooks/useScrollEnter';
import { usePageViewTracking } from '../../hooks/useTracking';

import { TaxonomyAdsTargetingProvider } from '../../contexts/AdsTargeting';
import { useGlobalBrand } from '../../contexts/LayoutContext';
import { useDefaultBrand } from '../../contexts/SiteContext';
import { typesSections } from '../../routes';
import HeaderAd from '../ads/HeaderAd';
import IndexDefaultHeader from '../headers/IndexDefaultHeader';
import InfiniteItemsBlocks from '../lists/InfiniteItemsBlocks';
import TypesMenu from '../menus/TypesMenu';
import NotFoundPage from './NotFoundPage';

import styles from '../../styles/pages/taxonomy-page.module.css';

export interface TaxonomyPageProps {
    taxonomy?: Topic | Category | Format | null;
    type?: string | null;
    notFound?: boolean;
    current?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    onEnter?: (() => void) | null;
    onLeave?: (() => void) | null;
}

function TaxonomyPage({
    taxonomy = null,
    type: typeSlug = null,
    notFound = false,
    current = false,
    className = null,
    contentClassName = null,
    onEnter = null,
    onLeave = null,
}: TaxonomyPageProps) {
    const type = typesSections.find(({ id, path }) => id === typeSlug || path === typeSlug);
    const { query: typeQuery } = type || {};
    const globalBrand = useGlobalBrand();
    const defaultBrand = useDefaultBrand();
    const { id: taxonomyId = null, type: taxonomyType, label, brand = null } = taxonomy || {};
    const finalBrand = brand || globalBrand || defaultBrand;
    const { handle: brandHandle = null } = finalBrand;
    const filters = useFiltersQuery();
    const search = useSearch();
    const { sort = null } = queryString.parse(search);

    useScrollEnter({
        disabled: !current,
        onEnter,
        onLeave,
    });

    usePageViewTracking(current && taxonomy !== null, {
        brand,
        categories: taxonomy !== null ? [taxonomy] : [],
        pageType: taxonomyType,
    });

    if (notFound) {
        return <NotFoundPage current={current} />;
    }

    return (
        <TaxonomyAdsTargetingProvider taxonomy={taxonomy}>
            <div
                className={classNames([styles.container, className, contentClassName])}
                data-brand={brandHandle}
            >
                <HeaderAd />
                <IndexDefaultHeader
                    title={label}
                    placeholder={taxonomy === null}
                    className={styles.header}
                />
                <TypesMenu
                    parent={taxonomy}
                    sort={sort}
                    withSort
                    withParent
                    className={styles.filters}
                />
                <main className={styles.content}>
                    <InfiniteItemsBlocks
                        query={{
                            [taxonomyType]: taxonomyId,
                            ...typeQuery,
                            ...filters,
                        }}
                        disabled={taxonomyId === null}
                        cardWith={['brand-icon']}
                    />
                </main>
            </div>
        </TaxonomyAdsTargetingProvider>
    );
}

export default TaxonomyPage;
